<template>
  <div
    v-if="sizes.length"
    class="size-chips"
    :class="{ 'spacing-experiment-wrapper' : isSpacingEnabled || isSpacing2Enabled }"
    data-testid="size-chips"
  >
    <div
      v-if="!isLatchkey"
      class="grid-x"
      :class="{
        'margin-bottom-xs': sizes.length > 1,
        'align-justify' : !isSpacingEnabled && !isSizeChartAlignLeft,
      }"
    >
      <div data-testid="size-label">
        <span
          class="label"
          :class="{ 'updated-label' : isSpacingExpEnabledForMcom }"
        >{{ label }}:</span>
        <template v-if="!isSizeDropdown">
          <span
            v-if="selectedSizeName"
            :class="[isMcom ? 'label' : 'medium', { 'updated-label': isSpacingExpEnabledForMcom }]"
            class="margin-left-xxxs"
          >
            {{ selectedSizeName }}
          </span>
          <span v-else>
            Please select
          </span>
        </template>
      </div>
      <div
        v-if="isSizeChart"
        class="grid-x"
        :class="{ 'margin-left-xs': isSpacingEnabled || isSizeChartAlignLeft }"
      >
        <span class="size-icon" />
        <button
          type="button"
          class="link-sm margin-left-xxxs"
          @click="onSizeGuideBtnClick"
        >
          Size Chart
        </button>
      </div>
    </div>
    <div v-if="isSizeDropdown">
      <div
        v-if="isLatchkey"
        class="grid-x"
      >
        <div class="cell auto">
          <span
            class="label"
            :class="{ 'updated-label' : isSpacingExpEnabledForMcom }"
          >{{ label }}:</span>
          <Dropdown
            :id="`size-type-dropdown-${name}`"
            v-model="selectedLatchkey"
            label="Size Type"
            :options="latchkeyOptions"
            label-size="none"
            @change="handleChange"
          />
        </div>
        <div
          v-if="sizes.length > latchkeyConfig.minCountToShowDropdown"
          class="grid-y cell auto margin-left-xs"
        >
          <div class="label grid-x align-justify latchkey-size-label">
            <div>
              {{ latchkeyConfig.label }}
            </div>
            <div>
              <div
                v-if="isSizeChart"
                class="grid-x"
              >
                <span class="size-icon" />
                <button
                  type="button"
                  class="link-sm margin-left-xxxs"
                  @click="onSizeGuideBtnClick"
                >
                  Size Chart
                </button>
              </div>
            </div>
          </div>
          <Dropdown
            :id="`size-dropdown-${name}`"
            v-model="selectedSizeIdString"
            class="col-6"
            label="Size"
            :options="dropdownOptions"
            label-size="none"
            @change="handleChange"
          />
        </div>
      </div>
      <Dropdown
        v-else
        :id="`size-dropdown-${name}`"
        v-model="selectedSizeIdString"
        label="Size"
        :options="dropdownOptions"
        label-size="none"
        @change="handleChange"
      />
    </div>
    <div
      v-else-if="sizes.length > 1"
      class="overflow-gutters-row"
    >
      <ExpandableList
        v-model="isExpanded"
        :active-item-index="selectedSizeIndex"
        :options="options"
        :row-height="isSpacingEnabled ? '40' : '50'"
        gap="8"
        :max-visible-rows="2"
        expanded-text="See all sizes"
        collapsed-text="See fewer sizes"
      >
        <SelectionTile
          v-model="selectedSizeId"
          class="tiles overflow-gutters-item"
          cell-spacing="shrink"
          size-tile
          :options="options"
          :name="name"
          @update:modelValue="onTouched"
        />
      </ExpandableList>
    </div>
    <slot
      name="after-chips"
      :size-chips="options"
    />
    <SizeGuideModal
      v-if="isSizeChart"
      :brand-name="brandName"
      :type-name="typeName"
      :size-chart-img="sizeChartImg"
      :name="modalName"
      :result="result"
      :is-loading="isLoading"
      :error="error"
    />
  </div>
</template>

<script setup>
import {
  computed, ref, defineAsyncComponent, watch,
} from 'vue';
import Dropdown from '@atomic-ui/dropdown';
import SelectionTile from '@atomic-ui/selectionTile';
import { latchkey as latchkeyConfig } from 'config';
import { useBrand } from '@composables/useBrand';
import { ExpandableList } from '../../../components/common';
import { tagSizesSeeAll, tagOnSizeDropdown } from '../../../analytics/sizes';
import { MODAL_SIZE_GUIDE } from '../../../components/modals/consts';
import useSizeChart from '../composables/useSizeChart';
import useModal from '../../../composables/useModal';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  modelValue: {
    /** @type import('vue').PropType<Number | null> */
    type: Number,
    default: null,
  },
  label: {
    type: String,
    default: 'Size',
  },
  latchkey: {
    /** @type import('vue').PropType<string | null> */
    type: String,
    default: null,
  },
  sizes: {
    /** @type import('vue').PropType<BaseSize[]> */
    type: Array,
    default: () => [],
  },
  latchkeys: {
    /** @type import('vue').PropType<string[]> */
    type: Array,
    default: () => [],
  },
  categoryData: {
    type: Object,
    default: () => ({ categoryID: '', topLevelCategoryID: '' }),
  },
  isSizeDropdown: {
    type: Boolean,
    default: false,
  },
  touched: {
    type: Boolean,
    default: false,
  },
  isSizeChart: {
    type: Boolean,
    default: false,
  },
  sizeChartImg: {
    type: String,
    default: '',
  },
  sizeChartId: {
    type: String,
    default: '',
  },
  productId: {
    type: String,
    required: true,
  },
  brandName: {
    type: String,
    required: true,
  },
  typeName: {
    type: String,
    default: '',
  },
  dropdownOptional: {
    type: Boolean,
    default: true,
  },
  isSpacingEnabled: {
    type: Boolean,
    default: false,
  },
  isSpacing2Enabled: {
    type: Boolean,
    default: false,
  },
  isSizeChartAlignLeft: {
    type: Boolean,
    default: false,
  },
});

const { open } = useModal();

const modalName = computed(() => (props.productId ? `${props.productId}-size-chart-modal` : MODAL_SIZE_GUIDE));

const {
  fetch, result, isLoading, error,
} = useSizeChart();

const onSizeGuideBtnClick = () => {
  if (!props.sizeChartImg) fetch({ productId: props.productId, chartId: props.sizeChartId });

  open(modalName.value);
};

const emits = defineEmits(['update:modelValue', 'update:touched', 'open:chartmodal', 'close:chartmodal', 'update:latchkey']);
const SizeGuideModal = defineAsyncComponent(() => import(/* webpackChunkName: "SizeGuideModal" */'../../../components/modals/SizeGuideModal'));
const { isMcom } = useBrand();

const selectedSizeId = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emits('update:modelValue', newValue);
  },
});

const selectedSizeIdString = computed({
  get() {
    return props.modelValue?.toString() || 'select-a-size';
  },
  set(newValue) {
    const numValue = Number(newValue);
    emits('update:modelValue', Number.isNaN(numValue) ? undefined : numValue);
  },
});

const selectedLatchkey = computed({
  get() {
    return props.latchkey || props.latchkeys[0] || null;
  },
  set(newValue) {
    emits('update:latchkey', newValue);
  },
});

const isExpanded = ref(false);

const localTouched = computed({
  get() {
    return props.touched;
  },
  set(newValue) {
    emits('update:touched', newValue);
  },
});

const selectedSizeName = computed(() => props.sizes.find((size) => size.id === selectedSizeId.value)?.name);

const isSpacingExpEnabledForMcom = isMcom && props.isSpacingEnabled;

const toOptionFormat = (size) => ({
  id: size.id, text: size.displayName, name: size.name, unavailable: !size.available,
});

const toDropdownFormat = (size) => ({
  value: size.id.toString(),
  displayValue: `${size.name}${!size.available ? ' (unavailable)' : ''}`,
});
const options = computed(() => props.sizes.map(toOptionFormat));
// TODO: temporary solution until platform team update atomic-ui dropdown component
const titleOption = { id: 'select-a-size', name: 'Select a size', available: true };
const dropdownOptions = computed(() => [
  ...(props.dropdownOptional ? [titleOption] : []),
  ...props.sizes,
].map(toDropdownFormat));

const handleChange = () => {
  tagOnSizeDropdown();
};

const selectedSizeIndex = computed(() => options.value.findIndex((option) => option.id === selectedSizeId.value));

const isLatchkey = computed(() => props.latchkeys.length);

const latchkeyOptions = computed(() => props.latchkeys.map((latchkey) => ({
  value: latchkey,
  displayValue: latchkey,
})));

const onTouched = () => {
  localTouched.value = true;
};

watch(isExpanded, tagSizesSeeAll);
</script>

<style lang="scss" src="../../../scss/utils/_overflow-gutters.scss" />

<style lang="scss" scoped>
.margin-left-xxxs {
  margin-left: calc(map-get($grid-margin-gutters, "small") / 4);
}

.size-chips {
  display: block;
}

.spacing-experiment-wrapper {
  .updated-label {
    font-weight: 400;
    text-transform: inherit;
  }

  :deep() {
    .selection-tile.size-tile {
        height: 40px;
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped></style>

<style lang="scss" brand="bcom" scoped>
@import "~@core/csg/src/scss/encodedIcons/bcom/_size.scss";

.size-icon {
  @extend .size;
  width: 16px;
  height: 16px;
  background-size: 16px;
}

.latchkey-size-label {
  height: 1.125rem // force height of empty label
}
</style>
